import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Shield, Users, Database, Eye, EyeOff, Trash2, ExternalLink, Search,
  ChevronLeft, ChevronRight, RefreshCw, LayoutDashboard, Loader2,
  Building2, Newspaper, CalendarDays, MessageSquare, Mail, Upload,
  ChevronDown, Globe, Plus, Pencil, X, Clock, Tag, Image, Save, Send
} from "lucide-react";
import { toast } from "sonner";
import { InvitationModule } from "@/components/InvitationModule";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES_LIST = [
  { value: "sektor-trendleri", label: "Sektör Trendleri" },
  { value: "teknoloji", label: "Teknoloji" },
  { value: "stand-tasarimi", label: "Stand Tasarımı" },
  { value: "lojistik", label: "Lojistik" },
  { value: "pazar-analizi", label: "Pazar Analizi" },
  { value: "surdurulebilirlik", label: "Sürdürülebilirlik" },
];

const MENU = [
  { id: "dashboard", label: "Dashboard", sub: "Genel Bakış", icon: LayoutDashboard },
  { id: "editor", label: "Haber Editörü", sub: "Yeni Haber Oluştur", icon: Pencil },
  { id: "manage-articles", label: "Haber Yönetimi", sub: "Tüm Haberler", icon: Newspaper },
  { id: "news-sources", label: "Haber Kaynakları", sub: "Kaynak Yönetimi", icon: Globe },
  { id: "users", label: "Kullanıcılar", sub: "Tüm Üyeler", icon: Users },
  { id: "invitation", label: "Davet Gönder", sub: "Kullanıcı Davet Et", icon: Send },
  { id: "collections", label: "Collections", sub: "MongoDB Yönetimi", icon: Database },
];

export default function UltraAdmin() {
  const [active, setActive] = useState("dashboard");
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [collections, setCollections] = useState([]);
  const [selectedCol, setSelectedCol] = useState(null);
  const [colDocs, setColDocs] = useState({ docs: [], total: 0 });
  const [colPage, setColPage] = useState(1);
  const [searchUser, setSearchUser] = useState("");
  const [loading, setLoading] = useState(true);

  // Article editor states
  const [editorForm, setEditorForm] = useState({ title: "", summary: "", content: "", category: "sektor-trendleri", image_url: "", tags: "", is_featured: false, homepage_hours: 0 });
  const [editingArticleId, setEditingArticleId] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [adminArticles, setAdminArticles] = useState({ articles: [], total: 0 });
  const [artPage, setArtPage] = useState(1);
  const [newsSources, setNewsSources] = useState([]);
  const [srcForm, setSrcForm] = useState({ name: "", url: "", type: "scraper", is_active: true, show_source: false });
  const [editingSrcId, setEditingSrcId] = useState(null);
  const [allProfiles, setAllProfiles] = useState([]);
  const [profileStats, setProfileStats] = useState({});
  const [profileSearch, setProfileSearch] = useState("");
  const [profileCategory, setProfileCategory] = useState("");
  const [visiblePasswords, setVisiblePasswords] = useState({});

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/stats`),
      axios.get(`${API}/admin/users`),
      axios.get(`${API}/admin/collections`),
      axios.get(`${API}/admin/news-sources`),
    ]).then(([s, u, c, ns]) => {
      setStats(s.data);
      setUsers(u.data);
      setCollections(c.data);
      setNewsSources(ns.data);
    }).finally(() => setLoading(false));
  }, []);

  const refreshUsers = async () => {
    const r = await axios.get(`${API}/admin/users`);
    setUsers(r.data);
  };

  const loadAdminArticles = async (page = 1) => {
    const r = await axios.get(`${API}/admin/articles?page=${page}&limit=15`);
    setAdminArticles(r.data);
    setArtPage(page);
  };

  const saveArticle = async () => {
    if (!editorForm.title) { toast.error("Başlık gerekli"); return; }
    const payload = { ...editorForm, tags: editorForm.tags.split(",").map(t => t.trim()).filter(Boolean), gallery_images: galleryImages };
    try {
      if (editingArticleId) {
        await axios.put(`${API}/admin/articles/${editingArticleId}`, payload);
        toast.success("Haber güncellendi!");
      } else {
        await axios.post(`${API}/admin/articles`, payload);
        toast.success("Haber yayınlandı!");
      }
      setEditorForm({ title: "", summary: "", content: "", category: "sektor-trendleri", image_url: "", tags: "", is_featured: false, homepage_hours: 0 });
      setGalleryImages([]);
      setEditingArticleId(null);
      loadAdminArticles(artPage);
    } catch { toast.error("Hata oluştu"); }
  };

  const startEditArticle = (a) => {
    setEditingArticleId(a.id);
    setEditorForm({
      title: a.title || "", summary: a.summary || "", content: a.content || "",
      category: a.category || "sektor-trendleri", image_url: a.image_url || "",
      tags: (a.tags || []).join(", "), is_featured: a.is_featured || false,
      homepage_hours: a.homepage_hours || 0,
    });
    setGalleryImages(a.gallery_images || []);
    setActive("editor");
    window.scrollTo(0, 0);
  };

  const toggleArticleActive = async (id, current) => {
    await axios.put(`${API}/admin/articles/${id}`, { is_active: !current });
    loadAdminArticles(artPage);
    toast.success(!current ? "Haber aktifleştirildi" : "Haber pasifleştirildi");
  };

  const deleteArticle = async (id) => {
    if (!window.confirm("Bu haberi silmek istediğinize emin misiniz?")) return;
    await axios.delete(`${API}/admin/articles/${id}`);
    loadAdminArticles(artPage);
    toast.success("Haber silindi");
  };

  const compressImage = (file, maxWidth = 800, quality = 0.7) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new window.Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let w = img.width, h = img.height;
          if (w > maxWidth) { h = (maxWidth / w) * h; w = maxWidth; }
          canvas.width = w;
          canvas.height = h;
          canvas.getContext("2d").drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL("image/jpeg", quality));
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleEditorImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const compressed = await compressImage(file, 1200, 0.8);
    setEditorForm(f => ({ ...f, image_url: compressed }));
  };

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = 8 - galleryImages.length;
    if (remaining <= 0) { toast.error("Maksimum 8 galeri görseli"); return; }
    const toProcess = files.slice(0, remaining);
    for (const file of toProcess) {
      const compressed = await compressImage(file, 800, 0.65);
      setGalleryImages((prev) => prev.length < 8 ? [...prev, compressed] : prev);
    }
    if (files.length > remaining) toast.info(`Max 8 görsel. ${files.length - remaining} dosya atlandı.`);
    e.target.value = "";
  };

  // News Sources
  const refreshSources = async () => {
    const r = await axios.get(`${API}/admin/news-sources`);
    setNewsSources(r.data);
  };

  const loadProfiles = async (search = "", cat = "") => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (cat) params.set("category", cat);
    const [pr, st] = await Promise.all([
      axios.get(`${API}/admin/all-profiles?${params}`),
      axios.get(`${API}/admin/profile-stats`),
    ]);
    setAllProfiles(pr.data);
    setProfileStats(st.data);
  };

  const toggleProfile = async (type, userId) => {
    const r = await axios.put(`${API}/admin/profiles/${type}/${userId}/toggle`);
    setAllProfiles(p => p.map(pr => pr.id === userId ? { ...pr, is_published: r.data.is_published } : pr));
    toast.success(r.data.is_published ? "Aktifleştirildi" : "Pasifleştirildi");
  };

  const deleteProfile = async (type, userId, name) => {
    if (!window.confirm(`"${name}" kullanıcısını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`)) return;
    try {
      await axios.delete(`${API}/admin/profiles/${type}/${userId}`);
      setAllProfiles(p => p.filter(pr => pr.id !== userId));
      setProfileStats(prev => ({
        ...prev,
        [type]: { total: (prev[type]?.total || 1) - 1, active: prev[type]?.active || 0 },
        total: (prev.total || 1) - 1
      }));
      toast.success("Kullanıcı silindi");
    } catch (err) {
      toast.error("Silme hatası");
    }
  };

  const saveSource = async () => {
    if (!srcForm.name || !srcForm.url) { toast.error("Ad ve URL gerekli"); return; }
    try {
      if (editingSrcId) {
        await axios.put(`${API}/admin/news-sources/${editingSrcId}`, srcForm);
        toast.success("Kaynak güncellendi");
      } else {
        await axios.post(`${API}/admin/news-sources`, srcForm);
        toast.success("Kaynak eklendi");
      }
      setSrcForm({ name: "", url: "", type: "scraper", is_active: true, show_source: false });
      setEditingSrcId(null);
      refreshSources();
    } catch { toast.error("Hata"); }
  };

  const toggleSource = async (id) => {
    const r = await axios.put(`${API}/admin/news-sources/${id}/toggle`);
    setNewsSources(p => p.map(s => s.id === id ? { ...s, is_active: r.data.is_active } : s));
    toast.success(r.data.is_active ? "Kaynak aktifleştirildi" : "Kaynak pasifleştirildi");
  };

  const deleteSource = async (id) => {
    if (!window.confirm("Bu kaynağı silmek istediğinize emin misiniz?")) return;
    await axios.delete(`${API}/admin/news-sources/${id}`);
    setNewsSources(p => p.filter(s => s.id !== id));
    toast.success("Kaynak silindi");
  };

  const toggleUser = async (id) => {
    const r = await axios.put(`${API}/admin/users/${id}/toggle`);
    setUsers((p) => p.map((u) => u.id === id ? { ...u, is_published: r.data.is_published } : u));
    toast.success(r.data.is_published ? "Aktifleştirildi" : "Pasifleştirildi");
  };

  const deleteUser = async (id) => {
    if (!window.confirm("Bu kullanıcıyı silmek istediğinize emin misiniz?")) return;
    await axios.delete(`${API}/admin/users/${id}`);
    setUsers((p) => p.filter((u) => u.id !== id));
    toast.success("Silindi");
  };

  const loadCol = async (name, page = 1) => {
    setSelectedCol(name);
    setColPage(page);
    setActive("col-view");
    const r = await axios.get(`${API}/admin/collections/${name}?page=${page}&limit=20`);
    setColDocs(r.data);
  };

  const deleteDoc = async (name, docId) => {
    if (!window.confirm("Kaydı silmek istediğinize emin misiniz?")) return;
    await axios.delete(`${API}/admin/collections/${name}/${docId}`);
    loadCol(name, colPage);
    toast.success("Silindi");
  };

  const filtered = searchUser
    ? users.filter((u) => u.email.includes(searchUser.toLowerCase()) || u.company_name.toLowerCase().includes(searchUser.toLowerCase()))
    : users;

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-stone-300" /></div>;

  return (
    <div className="flex min-h-screen bg-[#F8F9FA]" data-testid="ultra-admin">
      {/* ===== SIDEBAR ===== */}
      <aside className="w-56 bg-white border-r border-stone-200 flex flex-col flex-shrink-0 sticky top-0 h-screen overflow-y-auto">
        {/* Logo */}
        <div className="px-5 py-5 border-b border-stone-100">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#1B4F72]" />
            <div>
              <p className="font-bold text-sm text-stone-900">Ultra Admin</p>
              <p className="text-[10px] text-stone-400">FairStand Yönetim</p>
            </div>
          </div>
        </div>

        {/* Menu */}
        <nav className="flex-1 py-3 px-3 space-y-0.5">
          {MENU.map((item) => {
            const Icon = item.icon;
            const isActive = active === item.id || (item.id === "collections" && active === "col-view");
            return (
              <button
                key={item.id}
                onClick={() => setActive(item.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-left transition-colors ${
                  isActive ? "bg-[#1B4F72]/10 text-[#1B4F72]" : "text-stone-600 hover:bg-stone-50 hover:text-stone-900"
                }`}
                data-testid={`menu-${item.id}`}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs font-medium truncate">{item.label}</p>
                  <p className="text-[9px] text-stone-400 truncate">{item.sub}</p>
                </div>
              </button>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-stone-100">
          <Link to="/" className="text-xs text-stone-400 hover:text-[#1B4F72] transition-colors">
            Siteye Dön
          </Link>
        </div>
      </aside>

      {/* ===== MAIN CONTENT ===== */}
      <main className="flex-1 min-w-0">
        {/* Top bar */}
        <div className="bg-white border-b border-stone-200 px-8 py-4 flex items-center justify-between sticky top-0 z-10">
          <div>
            <h1 className="text-lg font-bold text-stone-900">
              {MENU.find((m) => m.id === active)?.label || (active === "col-view" ? selectedCol : "Dashboard")}
            </h1>
            <p className="text-xs text-stone-400">
              {MENU.find((m) => m.id === active)?.sub || (active === "col-view" ? "Koleksiyon Verileri" : "")}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-xs text-stone-400 hover:text-stone-900 border border-stone-200 px-3 py-1.5 rounded-md">
              <Globe className="w-3.5 h-3.5 inline mr-1" />Siteye Git
            </Link>
            <div className="w-8 h-8 bg-[#1B4F72] rounded-full flex items-center justify-center text-white text-xs font-bold">UA</div>
          </div>
        </div>

        <div className="p-8">
          {/* ===== DASHBOARD ===== */}
          {active === "dashboard" && (
            <div className="space-y-8">
              {/* Stat Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: "Toplam Kullanıcı", value: stats.users || 0, icon: Users, color: "bg-blue-50 text-blue-600" },
                  { label: "Aktif Firmalar", value: users.filter((u) => u.is_published).length, icon: Building2, color: "bg-green-50 text-green-600" },
                  { label: "Haberler", value: stats.articles || 0, icon: Newspaper, color: "bg-amber-50 text-amber-600" },
                  { label: "Etkinlikler", value: stats.events || 0, icon: CalendarDays, color: "bg-purple-50 text-purple-600" },
                ].map((s, i) => (
                  <div key={i} className="bg-white rounded-lg border border-stone-200 p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${s.color}`}>
                        <s.icon className="w-5 h-5" />
                      </div>
                    </div>
                    <p className="text-xs text-stone-500 mb-1">{s.label}</p>
                    <p className="text-2xl font-bold text-stone-900">{typeof s.value === "number" ? s.value.toLocaleString("tr-TR") : s.value}</p>
                  </div>
                ))}
              </div>

              {/* System status */}
              <div className="bg-white rounded-lg border border-stone-200 p-6">
                <h3 className="font-semibold text-stone-900 mb-4">Sistem Durumu</h3>
                {[
                  { name: "API Servisleri", status: true },
                  { name: "Veritabanı (MongoDB)", status: true },
                  { name: "Haber Scraper (APScheduler)", status: true },
                  { name: "AI Chatbot (GPT-5.2)", status: true },
                ].map((sys, i) => (
                  <div key={i} className="flex items-center justify-between py-3 border-b border-stone-100 last:border-0">
                    <span className="text-sm text-stone-700">{sys.name}</span>
                    <span className={`text-xs font-medium flex items-center gap-1.5 ${sys.status ? "text-green-600" : "text-red-600"}`}>
                      <span className={`w-2 h-2 rounded-full ${sys.status ? "bg-green-500" : "bg-red-500"}`} />
                      {sys.status ? "Aktif" : "Hata"}
                    </span>
                  </div>
                ))}
              </div>

              {/* Recent Users */}
              <div className="bg-white rounded-lg border border-stone-200 p-6">
                <h3 className="font-semibold text-stone-900 mb-4">Son Kayıt Olan Firmalar</h3>
                {users.slice(0, 5).map((u) => (
                  <div key={u.id} className="flex items-center justify-between py-2.5 border-b border-stone-100 last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-stone-100 rounded-full flex items-center justify-center text-xs font-bold text-stone-500">
                        {u.company_name?.[0] || "?"}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-stone-900">{u.company_name}</p>
                        <p className="text-[10px] text-stone-400">{u.email}</p>
                      </div>
                    </div>
                    <Badge className={`text-[9px] ${u.is_published ? "bg-green-100 text-green-700" : "bg-stone-100 text-stone-500"}`}>
                      {u.is_published ? "Aktif" : "Pasif"}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ===== USERS (Unified User Management) ===== */}
          {active === "users" && (() => {
            if (allProfiles.length === 0 && !profileSearch) loadProfiles();
            const TYPES = { "": "Tümü", firma: "Firmalar", organizator: "Organizatörler", freelancer: "Freelancerlar", tedarikci: "Tedarikçiler" };
            const typeColors = { firma: "bg-blue-100 text-blue-700", organizator: "bg-purple-100 text-purple-700", freelancer: "bg-amber-100 text-amber-700", tedarikci: "bg-teal-100 text-teal-700" };
            const typeLabels = { firma: "Firma", organizator: "Organizatör", freelancer: "Freelancer", tedarikci: "Tedarikçi" };
            const typeIcons = { firma: Building2, organizator: Globe, freelancer: Users, tedarikci: Database };

            return (
              <div className="space-y-6" data-testid="users-management">
                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                  <div className="bg-white rounded-lg border border-stone-200 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 bg-stone-100 rounded-lg flex items-center justify-center">
                        <Users className="w-4 h-4 text-stone-600" />
                      </div>
                    </div>
                    <p className="text-xs text-stone-500 mb-1">Toplam Üye</p>
                    <p className="text-2xl font-bold text-stone-900">{profileStats.total || 0}</p>
                  </div>
                  <div className="bg-white rounded-lg border border-stone-200 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                        <Eye className="w-4 h-4 text-green-600" />
                      </div>
                    </div>
                    <p className="text-xs text-stone-500 mb-1">Aktif Profil</p>
                    <p className="text-2xl font-bold text-green-600">{profileStats.total_active || 0}</p>
                  </div>
                  <div className="bg-white rounded-lg border border-stone-200 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Building2 className="w-4 h-4 text-blue-600" />
                      </div>
                    </div>
                    <p className="text-xs text-stone-500 mb-1">Firmalar</p>
                    <p className="text-xl font-bold text-stone-900">{profileStats.firma?.total || 0} <span className="text-xs font-normal text-green-600">({profileStats.firma?.active || 0})</span></p>
                  </div>
                  <div className="bg-white rounded-lg border border-stone-200 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                        <Globe className="w-4 h-4 text-purple-600" />
                      </div>
                    </div>
                    <p className="text-xs text-stone-500 mb-1">Organizatörler</p>
                    <p className="text-xl font-bold text-stone-900">{profileStats.organizator?.total || 0} <span className="text-xs font-normal text-green-600">({profileStats.organizator?.active || 0})</span></p>
                  </div>
                  <div className="bg-white rounded-lg border border-stone-200 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                        <Users className="w-4 h-4 text-amber-600" />
                      </div>
                    </div>
                    <p className="text-xs text-stone-500 mb-1">Freelancerlar</p>
                    <p className="text-xl font-bold text-stone-900">{profileStats.freelancer?.total || 0} <span className="text-xs font-normal text-green-600">({profileStats.freelancer?.active || 0})</span></p>
                  </div>
                  <div className="bg-white rounded-lg border border-stone-200 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
                        <Database className="w-4 h-4 text-teal-600" />
                      </div>
                    </div>
                    <p className="text-xs text-stone-500 mb-1">Tedarikçiler</p>
                    <p className="text-xl font-bold text-stone-900">{profileStats.tedarikci?.total || 0} <span className="text-xs font-normal text-green-600">({profileStats.tedarikci?.active || 0})</span></p>
                  </div>
                </div>

                {/* Search & Filter */}
                <div className="flex gap-3 flex-wrap items-center">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <Input 
                      value={profileSearch} 
                      onChange={e => { setProfileSearch(e.target.value); loadProfiles(e.target.value, profileCategory); }} 
                      placeholder="İsim, e-posta veya slug ile ara..." 
                      className="pl-10 rounded-md h-9 text-sm" 
                      data-testid="users-search"
                    />
                  </div>
                  <div className="flex gap-1 bg-stone-100 p-1 rounded-lg">
                    {Object.entries(TYPES).map(([val, label]) => (
                      <Button 
                        key={val} 
                        onClick={() => { setProfileCategory(val); loadProfiles(profileSearch, val); }}
                        variant="ghost"
                        size="sm"
                        className={`rounded-md h-7 text-xs px-3 ${profileCategory === val ? "bg-white shadow-sm text-[#1B4F72] font-medium" : "text-stone-500 hover:text-stone-700"}`}
                        data-testid={`filter-${val || 'all'}`}
                      >
                        {label}
                      </Button>
                    ))}
                  </div>
                  <Button onClick={() => loadProfiles(profileSearch, profileCategory)} variant="outline" size="sm" className="rounded-md h-9 gap-1">
                    <RefreshCw className="w-3.5 h-3.5" /> Yenile
                  </Button>
                </div>

                {/* Users Table */}
                <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
                  <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-stone-50 text-[10px] font-semibold uppercase tracking-wider text-stone-500 border-b">
                    <div className="col-span-1">Tür</div>
                    <div className="col-span-3">Kullanıcı</div>
                    <div className="col-span-2">E-posta</div>
                    <div className="col-span-2">Şifre</div>
                    <div className="col-span-2">Profil</div>
                    <div className="col-span-1">Durum</div>
                    <div className="col-span-1">İşlem</div>
                  </div>
                  {allProfiles.map((p) => {
                    const TypeIcon = typeIcons[p.type] || Users;
                    return (
                      <div key={`${p.type}-${p.id}`} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-stone-100 hover:bg-stone-50/50 items-center" data-testid={`user-row-${p.id}`}>
                        <div className="col-span-1">
                          <Badge className={`text-[8px] ${typeColors[p.type] || "bg-stone-100 text-stone-500"}`}>
                            {typeLabels[p.type] || p.type}
                          </Badge>
                        </div>
                        <div className="col-span-3 flex items-center gap-2">
                          {p.logo_url ? (
                            <img src={p.logo_url} alt="" className="w-8 h-8 rounded-full object-cover border border-stone-200" />
                          ) : (
                            <div className="w-8 h-8 bg-stone-100 rounded-full flex items-center justify-center text-xs font-bold text-stone-500 flex-shrink-0">
                              {p.name?.[0]?.toUpperCase() || "?"}
                            </div>
                          )}
                          <div className="min-w-0">
                            <span className="text-sm font-medium text-stone-900 truncate block">{p.name}</span>
                            <span className="text-[10px] text-stone-400">{p.city || "-"}</span>
                          </div>
                        </div>
                        <div className="col-span-2 text-xs text-stone-500 truncate">{p.email}</div>
                        <div className="col-span-2 flex items-center gap-1">
                          <span className="text-xs text-stone-400 font-mono truncate">
                            {visiblePasswords[p.id] ? (p.password_hash?.slice(0,16) || "N/A") : "••••••••"}
                          </span>
                          <button 
                            onClick={() => setVisiblePasswords(v => ({...v, [p.id]: !v[p.id]}))} 
                            className="text-stone-400 hover:text-stone-600 flex-shrink-0"
                            title={visiblePasswords[p.id] ? "Gizle" : "Göster"}
                          >
                            {visiblePasswords[p.id] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                        <div className="col-span-2">
                          <Link 
                            to={`/${p.type === "firma" ? "firma" : p.type === "organizator" ? "organizator" : p.type === "freelancer" ? "freelancer" : "tedarikci"}/${p.slug}`} 
                            target="_blank" 
                            className="text-xs text-[#1B4F72] hover:underline truncate block"
                          >
                            /{p.slug}
                          </Link>
                        </div>
                        <div className="col-span-1">
                          <button onClick={() => toggleProfile(p.type, p.id)} data-testid={`toggle-${p.id}`}>
                            <Badge className={`text-[8px] cursor-pointer transition-colors ${p.is_published ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-red-100 text-red-600 hover:bg-red-200"}`}>
                              {p.is_published ? "Aktif" : "Pasif"}
                            </Badge>
                          </button>
                        </div>
                        <div className="col-span-1 flex gap-1">
                          <Link to={`/${p.type === "firma" ? "firma" : p.type === "organizator" ? "organizator" : p.type === "freelancer" ? "freelancer" : "tedarikci"}/${p.slug}`} target="_blank">
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-stone-400 hover:text-[#1B4F72]" title="Profili Görüntüle">
                              <ExternalLink className="w-3.5 h-3.5" />
                            </Button>
                          </Link>
                          <Button 
                            onClick={() => deleteProfile(p.type, p.id, p.name)} 
                            variant="ghost" 
                            size="icon" 
                            className="h-7 w-7 text-stone-400 hover:text-red-500" 
                            title="Kullanıcıyı Sil"
                            data-testid={`delete-${p.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                  {allProfiles.length === 0 && (
                    <div className="px-4 py-12 text-center">
                      <Users className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                      <p className="text-stone-500 text-sm">Kullanıcı bulunamadı</p>
                      <p className="text-stone-400 text-xs mt-1">Farklı bir arama terimi deneyin</p>
                    </div>
                  )}
                </div>

                {/* Summary */}
                <div className="flex justify-between items-center text-xs text-stone-400">
                  <span>Toplam {allProfiles.length} kullanıcı gösteriliyor</span>
                  <span>Son güncelleme: {new Date().toLocaleTimeString("tr-TR")}</span>
                </div>
              </div>
            );
          })()}

          {/* ===== ARTICLE EDITOR ===== */}
          {active === "editor" && (
            <div className="space-y-6">
              <div className="bg-white rounded-lg border border-stone-200 p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-stone-900">{editingArticleId ? "Haberi Düzenle" : "Yeni Haber Oluştur"}</h3>
                  {editingArticleId && (
                    <Button onClick={() => { setEditingArticleId(null); setGalleryImages([]); setEditorForm({ title: "", summary: "", content: "", category: "sektor-trendleri", image_url: "", tags: "", is_featured: false, homepage_hours: 0 }); }} variant="ghost" size="sm" className="gap-1 text-stone-400"><X className="w-3.5 h-3.5" /> İptal</Button>
                  )}
                </div>

                <div>
                  <Label className="text-xs text-stone-500">Haber Başlığı *</Label>
                  <Input value={editorForm.title} onChange={(e) => setEditorForm({ ...editorForm, title: e.target.value })} className="rounded-md mt-1 text-lg font-semibold" placeholder="Dikkat çekici başlık yazın..." data-testid="editor-title" />
                </div>

                <div>
                  <Label className="text-xs text-stone-500">Kısa Özet (Ana sayfada görünür)</Label>
                  <Textarea value={editorForm.summary} onChange={(e) => setEditorForm({ ...editorForm, summary: e.target.value })} rows={2} className="rounded-md mt-1" placeholder="2-3 cümlelik özet..." data-testid="editor-summary" />
                </div>

                <div>
                  <Label className="text-xs text-stone-500">Haber İçeriği (Detay sayfası)</Label>
                  <Textarea value={editorForm.content} onChange={(e) => setEditorForm({ ...editorForm, content: e.target.value })} rows={12} className="rounded-md mt-1 font-mono text-sm" placeholder="Paragraflar arasında boş satır bırakın..." data-testid="editor-content" />
                  <p className="text-[10px] text-stone-400 mt-1">{editorForm.content.length} karakter · Paragraflar için boş satır bırakın</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs text-stone-500">Kategori</Label>
                    <select value={editorForm.category} onChange={(e) => setEditorForm({ ...editorForm, category: e.target.value })} className="w-full mt-1 h-9 rounded-md border border-stone-200 px-3 text-sm" data-testid="editor-category">
                      {CATEGORIES_LIST.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-stone-500 flex items-center gap-1"><Clock className="w-3 h-3" /> Ana Sayfada Kalma Süresi</Label>
                    <select value={editorForm.homepage_hours} onChange={(e) => setEditorForm({ ...editorForm, homepage_hours: parseInt(e.target.value) })} className="w-full mt-1 h-9 rounded-md border border-stone-200 px-3 text-sm" data-testid="editor-hours">
                      <option value={0}>Süresiz (Editör haberlerinde kalır)</option>
                      <option value={6}>6 saat</option>
                      <option value={12}>12 saat</option>
                      <option value={24}>24 saat</option>
                      <option value={48}>48 saat</option>
                      <option value={72}>72 saat</option>
                      <option value={168}>1 hafta</option>
                    </select>
                  </div>
                  <div className="flex items-end gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={editorForm.is_featured} onChange={(e) => setEditorForm({ ...editorForm, is_featured: e.target.checked })} className="rounded" />
                      <span className="text-xs text-stone-600">Manşet Haber</span>
                    </label>
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-stone-500 flex items-center gap-1"><Tag className="w-3 h-3" /> Etiketler (virgülle ayırın)</Label>
                  <Input value={editorForm.tags} onChange={(e) => setEditorForm({ ...editorForm, tags: e.target.value })} className="rounded-md mt-1" placeholder="fuar, stand, exhibition, istanbul" data-testid="editor-tags" />
                </div>

                <div>
                  <Label className="text-xs text-stone-500 flex items-center gap-1"><Image className="w-3 h-3" /> Haber Görseli</Label>
                  <div className="flex gap-3 mt-1 items-start">
                    <Input value={editorForm.image_url} onChange={(e) => setEditorForm({ ...editorForm, image_url: e.target.value })} className="rounded-md flex-1" placeholder="URL yapıştırın veya yükleyin..." />
                    <label className="cursor-pointer">
                      <input type="file" accept="image/*" className="hidden" onChange={handleEditorImage} />
                      <Button variant="outline" size="sm" className="rounded-md gap-1" asChild><span><Upload className="w-3.5 h-3.5" /> Yükle</span></Button>
                    </label>
                  </div>
                  {editorForm.image_url && (
                    <div className="mt-2 relative inline-block">
                      <img src={editorForm.image_url} alt="" className="h-32 object-cover rounded border" />
                      <button onClick={() => setEditorForm({ ...editorForm, image_url: "" })} className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center"><X className="w-3 h-3" /></button>
                    </div>
                  )}
                </div>

                {/* Gallery Images */}
                <div>
                  <Label className="text-xs text-stone-500 flex items-center gap-1"><Image className="w-3 h-3" /> İlave Galeri Görselleri (max 8)</Label>
                  <p className="text-[10px] text-stone-400 mb-2">Haber içeriğinin altında 4+4 grid olarak gösterilecek</p>
                  <div className="grid grid-cols-4 gap-2">
                    {galleryImages.map((img, i) => (
                      <div key={i} className="aspect-[4/3] relative border border-stone-200 rounded overflow-hidden group">
                        <img src={img} alt="" className="w-full h-full object-cover" />
                        <button onClick={() => setGalleryImages(galleryImages.filter((_, j) => j !== i))} className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
                        <span className="absolute bottom-1 left-1 bg-black/50 text-white text-[8px] px-1 rounded">{i + 1}/8</span>
                      </div>
                    ))}
                    {galleryImages.length < 8 && (
                      <label className="aspect-[4/3] border-2 border-dashed border-stone-300 rounded flex flex-col items-center justify-center cursor-pointer hover:border-[#1B4F72] transition-colors">
                        <input type="file" accept="image/*" multiple className="hidden" onChange={handleGalleryUpload} />
                        <Plus className="w-5 h-5 text-stone-400" />
                        <span className="text-[9px] text-stone-400 mt-1">{galleryImages.length}/8</span>
                      </label>
                    )}
                  </div>
                </div>

                <Separator />

                <div className="flex gap-3">
                  <Button onClick={saveArticle} className="rounded-md bg-[#1B4F72] hover:bg-[#154360] gap-2 px-6" data-testid="editor-save">
                    <Save className="w-4 h-4" /> {editingArticleId ? "Güncelle" : "Yayınla"}
                  </Button>
                  {editingArticleId && (
                    <Button onClick={() => { setEditingArticleId(null); setGalleryImages([]); setEditorForm({ title: "", summary: "", content: "", category: "sektor-trendleri", image_url: "", tags: "", is_featured: false, homepage_hours: 0 }); }} variant="outline" className="rounded-md">İptal</Button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ===== MANAGE ARTICLES ===== */}
          {active === "manage-articles" && (() => {
            if (adminArticles.articles.length === 0) loadAdminArticles();
            
            // Haberleri kaynak türüne göre ayır
            const editorArticles = adminArticles.articles.filter(a => a.source === "Editör" || a.source === "EDITÖR" || a.source?.toLowerCase() === "editör");
            const scrapedArticles = adminArticles.articles.filter(a => a.source !== "Editör" && a.source !== "EDITÖR" && a.source?.toLowerCase() !== "editör");
            
            const ArticleTable = ({ articles, title, emptyText, bgColor = "bg-white" }) => (
              <div className={`${bgColor} rounded-lg border border-stone-200 overflow-hidden`}>
                <div className="px-4 py-3 bg-stone-50 border-b flex items-center justify-between">
                  <h4 className="font-semibold text-stone-900 text-sm">{title} ({articles.length})</h4>
                </div>
                <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-stone-50/50 text-[10px] font-medium uppercase tracking-wider text-stone-500 border-b">
                  <div className="col-span-4">Başlık</div>
                  <div className="col-span-2">Kaynak</div>
                  <div className="col-span-2">Kategori</div>
                  <div className="col-span-1">İçerik</div>
                  <div className="col-span-1">Durum</div>
                  <div className="col-span-2">İşlem</div>
                </div>
                {articles.length > 0 ? articles.map((a) => (
                  <div key={a.id} className="grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-stone-100 hover:bg-stone-50/50 items-center text-sm">
                    <div className="col-span-4 truncate font-medium text-stone-900">{a.title}</div>
                    <div className="col-span-2 text-xs text-stone-400 truncate">{a.source}</div>
                    <div className="col-span-2"><Badge variant="outline" className="text-[8px]">{a.category}</Badge></div>
                    <div className="col-span-1 text-[10px] text-stone-400">{(a.content || "").length} chr</div>
                    <div className="col-span-1">
                      <button onClick={() => toggleArticleActive(a.id, a.is_active !== false)}>
                        <Badge className={`text-[8px] cursor-pointer ${a.is_active !== false ? "bg-green-100 text-green-700" : "bg-stone-100 text-stone-400"}`}>
                          {a.is_active !== false ? "Aktif" : "Pasif"}
                        </Badge>
                      </button>
                    </div>
                    <div className="col-span-2 flex gap-1">
                      <Button onClick={() => startEditArticle(a)} variant="ghost" size="icon" className="h-7 w-7 text-stone-400 hover:text-[#1B4F72]"><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button onClick={() => deleteArticle(a.id)} variant="ghost" size="icon" className="h-7 w-7 text-stone-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></Button>
                      <Link to={`/haber/${a.slug || a.id}`} target="_blank"><Button variant="ghost" size="icon" className="h-7 w-7 text-stone-400 hover:text-blue-500"><ExternalLink className="w-3.5 h-3.5" /></Button></Link>
                    </div>
                  </div>
                )) : (
                  <div className="px-4 py-8 text-center text-stone-400 text-sm">{emptyText}</div>
                )}
              </div>
            );
            
            return (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-stone-900">Tüm Haberler ({adminArticles.total})</h3>
                  <div className="flex gap-2">
                    <Button onClick={() => loadAdminArticles(artPage)} variant="outline" size="sm" className="rounded-md gap-1"><RefreshCw className="w-3.5 h-3.5" /> Yenile</Button>
                    <Button onClick={() => setActive("editor")} size="sm" className="rounded-md bg-[#1B4F72] hover:bg-[#154360] gap-1"><Plus className="w-3.5 h-3.5" /> Yeni Haber</Button>
                  </div>
                </div>

                {/* Editör Haberleri */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-3 h-3 bg-[#1B4F72] rounded-full"></div>
                    <span className="text-sm font-medium text-stone-700">Editör Haberleri</span>
                  </div>
                  <ArticleTable 
                    articles={editorArticles} 
                    title="Editör Haberleri" 
                    emptyText="Editör tarafından oluşturulan haber yok"
                    bgColor="bg-blue-50/30"
                  />
                </div>

                {/* Scraped Haberler */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
                    <span className="text-sm font-medium text-stone-700">Web Scraper Haberleri</span>
                  </div>
                  <ArticleTable 
                    articles={scrapedArticles} 
                    title="Scraped Haberler" 
                    emptyText="Scrape edilmiş haber yok"
                  />
                </div>

                {/* Pagination */}
                <div className="flex justify-center gap-2">
                  <Button onClick={() => loadAdminArticles(artPage - 1)} disabled={artPage <= 1} variant="outline" size="sm" className="rounded-md"><ChevronLeft className="w-4 h-4" /></Button>
                  <span className="text-xs text-stone-500 py-2">{artPage} / {Math.ceil(adminArticles.total / 15) || 1}</span>
                  <Button onClick={() => loadAdminArticles(artPage + 1)} disabled={artPage >= Math.ceil(adminArticles.total / 15)} variant="outline" size="sm" className="rounded-md"><ChevronRight className="w-4 h-4" /></Button>
                </div>
              </div>
            );
          })()}

          {/* ===== NEWS SOURCES ===== */}
          {active === "news-sources" && (
            <div className="space-y-6">
              {/* Add/Edit Source Form */}
              <div className="bg-white rounded-lg border border-stone-200 p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-stone-900">{editingSrcId ? "Kaynağı Düzenle" : "Yeni Kaynak Ekle"}</h3>
                  {editingSrcId && <Button onClick={() => { setEditingSrcId(null); setSrcForm({ name: "", url: "", type: "scraper", is_active: true }); }} variant="ghost" size="sm" className="gap-1 text-stone-400"><X className="w-3.5 h-3.5" /> İptal</Button>}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs text-stone-500">Kaynak Adı *</Label>
                    <Input value={srcForm.name} onChange={(e) => setSrcForm({ ...srcForm, name: e.target.value })} className="rounded-md mt-1" placeholder="ör: TÜYAP" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs text-stone-500">URL *</Label>
                    <Input value={srcForm.url} onChange={(e) => setSrcForm({ ...srcForm, url: e.target.value })} className="rounded-md mt-1" placeholder="https://..." />
                  </div>
                </div>
                <div className="flex gap-3 items-end">
                  <div>
                    <Label className="text-xs text-stone-500">Tür</Label>
                    <select value={srcForm.type} onChange={(e) => setSrcForm({ ...srcForm, type: e.target.value })} className="mt-1 h-9 rounded-md border border-stone-200 px-3 text-sm">
                      <option value="scraper">Web Scraper</option>
                      <option value="rss">RSS Feed</option>
                      <option value="api">API</option>
                    </select>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer h-9">
                    <input type="checkbox" checked={srcForm.show_source} onChange={(e) => setSrcForm({ ...srcForm, show_source: e.target.checked })} className="rounded" />
                    <span className="text-xs text-stone-600">Kaynak Göster</span>
                  </label>
                  <Button onClick={saveSource} className="rounded-md bg-[#1B4F72] hover:bg-[#154360] gap-1.5">
                    {editingSrcId ? <><Save className="w-4 h-4" /> Güncelle</> : <><Plus className="w-4 h-4" /> Ekle</>}
                  </Button>
                </div>
              </div>

              {/* Sources List */}
              <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
                <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-stone-50 text-[10px] font-medium uppercase tracking-wider text-stone-500 border-b">
                  <div className="col-span-2">Kaynak</div>
                  <div className="col-span-4">URL</div>
                  <div className="col-span-1">Tür</div>
                  <div className="col-span-1">Telif</div>
                  <div className="col-span-2">Durum</div>
                  <div className="col-span-2">İşlem</div>
                </div>
                {newsSources.map((src) => (
                  <div key={src.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-stone-100 hover:bg-stone-50/50 items-center">
                    <div className="col-span-2 text-sm font-medium text-stone-900">{src.name}</div>
                    <div className="col-span-4 text-xs text-stone-400 truncate">
                      <a href={src.url} target="_blank" rel="noopener noreferrer" className="hover:text-[#1B4F72]">{src.url}</a>
                    </div>
                    <div className="col-span-1"><Badge variant="outline" className="text-[8px]">{src.type}</Badge></div>
                    <div className="col-span-1">
                      {src.show_source && <Badge className="bg-amber-100 text-amber-700 text-[8px]">Kaynak</Badge>}
                    </div>
                    <div className="col-span-1">
                      <button onClick={() => toggleSource(src.id)}>
                        <Badge className={`text-[8px] cursor-pointer ${src.is_active ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-stone-100 text-stone-400 hover:bg-stone-200"}`}>
                          {src.is_active ? "Aktif" : "Pasif"}
                        </Badge>
                      </button>
                    </div>
                    <div className="col-span-2 flex gap-1">
                      <Button onClick={() => { setEditingSrcId(src.id); setSrcForm({ name: src.name, url: src.url, type: src.type, is_active: src.is_active, show_source: src.show_source || false }); }} variant="ghost" size="icon" className="h-7 w-7 text-stone-400 hover:text-[#1B4F72]"><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button onClick={() => deleteSource(src.id)} variant="ghost" size="icon" className="h-7 w-7 text-stone-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ===== INVITATION MODULE ===== */}
          {active === "invitation" && (
            <div className="max-w-2xl">
              <InvitationModule />
            </div>
          )}

          {/* ===== COLLECTIONS LIST ===== */}
          {active === "collections" && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {collections.map((col) => (
                <button
                  key={col.name}
                  onClick={() => loadCol(col.name)}
                  className="bg-white rounded-lg border border-stone-200 p-5 text-left hover:border-[#1B4F72] hover:shadow-sm transition-all group"
                  data-testid={`col-${col.name}`}
                >
                  <Database className="w-5 h-5 text-stone-300 group-hover:text-[#1B4F72] mb-3 transition-colors" />
                  <p className="font-medium text-sm text-stone-900">{col.name}</p>
                  <p className="text-2xl font-bold text-stone-900 mt-1">{col.count.toLocaleString("tr-TR")}</p>
                  <p className="text-[10px] text-stone-400 mt-1">kayıt</p>
                </button>
              ))}
            </div>
          )}

          {/* ===== COLLECTION VIEW ===== */}
          {active === "col-view" && selectedCol && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button onClick={() => setActive("collections")} variant="ghost" size="sm" className="h-7 px-2 text-stone-400">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <h2 className="font-semibold text-stone-900">{selectedCol}</h2>
                  <Badge variant="outline" className="text-[9px]">{colDocs.total} kayıt</Badge>
                </div>
                <div className="flex items-center gap-1">
                  <Button onClick={() => loadCol(selectedCol, colPage - 1)} disabled={colPage <= 1} variant="outline" size="icon" className="h-7 w-7 rounded-md"><ChevronLeft className="w-4 h-4" /></Button>
                  <span className="text-xs text-stone-500 px-2">{colPage}/{Math.ceil(colDocs.total / 20) || 1}</span>
                  <Button onClick={() => loadCol(selectedCol, colPage + 1)} disabled={colPage >= Math.ceil(colDocs.total / 20)} variant="outline" size="icon" className="h-7 w-7 rounded-md"><ChevronRight className="w-4 h-4" /></Button>
                </div>
              </div>

              <div className="space-y-2">
                {colDocs.docs.map((doc, idx) => {
                  const entries = Object.entries(doc).filter(([k]) => k !== "_id").slice(0, 8);
                  return (
                    <div key={idx} className="bg-white rounded-lg border border-stone-200 p-4 group hover:border-stone-300 transition-colors">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] text-stone-400 font-mono">{doc.id || doc.slug || `#${idx + 1}`}</span>
                        {doc.id && (
                          <Button onClick={() => deleteDoc(selectedCol, doc.id)} variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100 text-stone-400 hover:text-red-500">
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2">
                        {entries.map(([key, val]) => (
                          <div key={key} className="min-w-0">
                            <p className="text-[9px] uppercase tracking-wider text-stone-400 mb-0.5">{key}</p>
                            <p className="text-xs text-stone-700 truncate">
                              {typeof val === "boolean" ? (val ? "true" : "false") :
                               Array.isArray(val) ? `[${val.length}]` :
                               typeof val === "object" ? "{...}" :
                               String(val || "-").slice(0, 100)}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
